import { WorkflowAction } from './WorkflowAction';

// The status the integration reports on sex_offender_screen_result:
//   - 'match' — offenders.io returned candidate records; the action settles `completed` and
//     the API's #effective_status folds it to `flagged` for the referral UI to attend (mirrors
//     DenialCheck). Rule C: the flag burns off once every candidate is dismissed.
//   - 'clear' — no matches; the action completes green.
//   - 'error' — offenders.io failed; the action errored.
export const SEX_OFFENDER_RESULT = { MATCH: 'match', CLEAR: 'clear', ERROR: 'error' } as const;

// Per-match adjudication decisions the FE writes to sex_offender_match_reviews. Every click
// (first or Nth, Match or Dismiss) is a save_progress write to action.outputs — no lifecycle
// transition, since the integration always settles the action to `completed` on a terminal
// result. The FE reads them back off action.outputs (never a workflow value).
export const REVIEW_DECISION = { MATCH: 'match', DISMISS: 'dismiss' } as const;
export type ReviewDecision = (typeof REVIEW_DECISION)[keyof typeof REVIEW_DECISION];

// Buckets the integration's scoring module (olio-integration app/apis/offendercheck/scoring.py)
// stamps on each match at driver time. `strong` and `plausible` are the "possible match" set that
// drives the row's flagged treatment; `unlikely` and `very_unlikely` are noise-only per the
// server-side effective_status rules. Keep this list in sync with the Rails constant
// SexOffenderCheck::BUCKET_* (same string values, same set semantics).
export const MATCH_BUCKET = {
  STRONG: 'strong',
  PLAUSIBLE: 'plausible',
  UNLIKELY: 'unlikely',
  VERY_UNLIKELY: 'very_unlikely',
} as const;
export type MatchBucket = (typeof MATCH_BUCKET)[keyof typeof MATCH_BUCKET];

// Buckets that raise the "possible match" flag when un-dismissed. Mirrors Rails
// SexOffenderCheck::ATTENTION_BUCKETS.
export const ATTENTION_BUCKETS: readonly MatchBucket[] = [MATCH_BUCKET.STRONG, MATCH_BUCKET.PLAUSIBLE];

// Reviewer-facing render order for bucket sections in the slideout.
export const BUCKET_RENDER_ORDER: readonly MatchBucket[] = [
  MATCH_BUCKET.STRONG,
  MATCH_BUCKET.PLAUSIBLE,
  MATCH_BUCKET.UNLIKELY,
  MATCH_BUCKET.VERY_UNLIKELY,
];

export interface MatchReview {
  // The offenders.io UUIDv5 of the match this decision applies to. Kept as a VALUE (not a
  // hash key) so OliveBranch's kebab/snake/camel transforms don't mangle it — UUIDs contain
  // dashes and would round-trip differently on each request (see OLIO-2391 comment history).
  uuid: string;
  decision: ReviewDecision;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

// Reviews are an array on the wire and internally — one entry per adjudicated match. Callers
// look up by uuid via `reviewFor()` on the action model rather than array-indexing.
export type MatchReviews = MatchReview[];

// Structured per-crime entry from offenders.io extensive mode (stateData.offenses[]). Field names
// mirror the API shape verbatim (offense / convictionDate / jurisdiction) — earlier renames to
// description/date/location silently produced empty crime bullets. The `offense` string can itself
// contain embedded `\n` characters for records the aggregator couldn't normalize (VA in
// particular), so render it with `white-space: pre-line`. All fields are per-registry optional —
// Arizona often returns `offense` alone, some sources add `statute`/`caseNumber`/`releaseDate`.
export interface OffenderOffense {
  offense?: string | null;
  convictionDate?: string | null;
  jurisdiction?: string | null;
  statute?: string | null;
  caseNumber?: string | null;
  releaseDate?: string | null;
  [key: string]: unknown;
}

export interface OffenderStateData {
  status?: string | null; // "Active" / etc. — badge on the match card
  designation?: string | null; // e.g. "Level 1 Predator" — badge on the match card
  offenses?: OffenderOffense[];
  [key: string]: unknown;
}

export interface OffenderSource {
  id?: string;
  name?: string | null;
  url?: string | null;
}

// A single sex-offender registry candidate as it comes back from the integration platform
// (offenders.io response passed through verbatim). All fields are optional — the mockup renders
// "–" for absent values.
export interface OffenderMatch {
  uuid: string; // UUIDv5 pass-through — the stable key for adjudication (OLIO-2391)
  name?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  dob?: string | null;
  age?: string | null;
  sex?: string | null;
  race?: string | null;
  ethnicity?: string | null;
  hairColor?: string | null;
  eyeColor?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  crime?: string | null;
  riskLevel?: string | null;
  isPredator?: boolean | null;
  isAbsconder?: boolean | null;
  offenderUrl?: string | null;
  offenderImageUrl?: string | null;
  offenderImageUrlR2?: string | null;
  sources?: OffenderSource[];
  stateData?: OffenderStateData | null;
  // Client-side scoring fields computed at driver time by the integration's scoring module
  // (see olio-integration app/apis/offendercheck/scoring.py + OFFENDERS_IO_NOTES §5). Frozen
  // into the result body at review time so both consumer surfaces render identical adjudication
  // signals and historical checks stay locked to the weights that existed then.
  //
  // Rails camelCases the wire fields via OliveBranch (snake_case in Python → camelCase here).
  // 'F' is the fallback and holds two populations: a surname-only hit, and a record the
  // integration's scorer couldn't explain at all. A separate 'S' tier for the former was
  // built and removed — it changed no outcome, since both are withheld the same way.
  nameTier?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | null;
  nameScore?: number | null;
  dobDeltaYears?: number | null;
  dobScore?: number | null;
  // Penalty-only: 0, or negative when the patient's sex and the registrant's are both known
  // and differ. Never positive — with registries overwhelmingly male, agreement is the default
  // state of the data and carries almost no information (OLIO-3173).
  sexScore?: number | null;
  matchScore?: number | null;
  matchBucket?: MatchBucket | null;
  // Which generation of the integration's weights/tiers/withholding rules produced the fields
  // above. Absent on pre-OLIO-3173 records, which are version 1. Matters because candidates the
  // integration deems not credible are now withheld from this payload entirely, so a historical
  // check can only be read correctly against the rules that were in force when it ran.
  scoringVersion?: number | null;
  [key: string]: unknown;
}

interface ScreenResult {
  status?: string;
  matches?: OffenderMatch[];
  // How many candidates offenders.io RETURNED, which is not the same as `matches.length`:
  // the integration caps the payload at 100, and as of OLIO-3173 also withholds candidates
  // it judges not credible. Sent since the cap was introduced but never declared here.
  totalMatchCount?: number;
  // How many of `totalMatchCount` were withheld and deliberately never sent (OLIO-3173).
  // Declared so the contract is honest and support can read it; nothing renders it.
  withheldCount?: number;
  error?: string;
}

// The SexOffenderCheck action kind. offenders.io returns candidate matches when demographics
// hit a registry; the integration always settles the action to `completed` on a terminal
// result (matches or clear). A person adjudicates each candidate Dismiss/Match in the
// slideout; every decision is a save_progress write to `action.outputs['sex_offender_match_reviews']`
// via saveWorkflowActionProgress — no lifecycle transition, no first-vs-subsequent distinction.
//
// Presentation folds server-side (SexOffenderCheck#effective_status): `completed + match →
// flagged`, burning off to `completed` when every candidate is dismissed (Rule C). This model
// exposes only the data the FE renders.
export class SexOffenderCheckAction extends WorkflowAction {
  // The screen result rides on action.outputs (echoed by the FE alongside reviews on every
  // save_progress write) and on the workflow value the integration callback published. In-model
  // we only see action.outputs; workflow-value fallback isn't needed because the FE echoes it.
  private get result(): ScreenResult {
    const value = this.outputFor('sex_offender_screen_result');
    return value && typeof value === 'object' ? (value as ScreenResult) : {};
  }

  get screenResult(): ScreenResult {
    return this.result;
  }

  get determination(): string | undefined {
    return this.result.status;
  }

  get isMatch(): boolean {
    return this.determination === SEX_OFFENDER_RESULT.MATCH;
  }

  get matches(): OffenderMatch[] {
    const matches = this.result.matches;
    return Array.isArray(matches) ? matches : [];
  }

  // Per-match reviews recorded so far (empty until the first Dismiss / Match click). An array
  // of `{uuid, decision, ...}` — the FE overlays it onto matches[] to render per-card state.
  // Wire shape is intentionally array (see MatchReview docstring).
  get reviews(): MatchReviews {
    const value = this.outputFor('sex_offender_match_reviews');
    return Array.isArray(value) ? (value as MatchReviews) : [];
  }

  // Whether any per-match decision has been recorded — signals the caller to use
  // saveWorkflowActionProgress (patch-only) instead of completeWorkflowAction (transition).
  get hasAnyReview(): boolean {
    return this.reviews.length > 0;
  }

  reviewFor(uuid: string): MatchReview | undefined {
    return this.reviews.find((r) => r.uuid === uuid);
  }

  // Matches the FE renders as active (default state). Dismissed cards move behind the
  // "View Dismissed Result" affordance; matched cards render in a distinct highlighted style.
  get activeMatches(): OffenderMatch[] {
    return this.matches.filter((m) => this.reviewFor(m.uuid)?.decision !== REVIEW_DECISION.DISMISS);
  }

  get dismissedMatches(): OffenderMatch[] {
    return this.matches.filter((m) => this.reviewFor(m.uuid)?.decision === REVIEW_DECISION.DISMISS);
  }

  get matchedMatches(): OffenderMatch[] {
    return this.matches.filter((m) => this.reviewFor(m.uuid)?.decision === REVIEW_DECISION.MATCH);
  }

  // Strong + plausible matches, regardless of adjudication. Drives the row-summary copy
  // ("N possible matches found out of M results") — N is the number the reviewer actually cares
  // about at a glance; the "M results" total is exposed as context but is not the alert signal.
  // A missing bucket counts as strong (safest default for compliance — matches the server-side
  // resolver's behavior for legacy or un-scored records).
  get strongOrPlausibleMatches(): OffenderMatch[] {
    return this.matches.filter((m) => {
      const b = (m.matchBucket ?? '').trim();
      return b === '' || (ATTENTION_BUCKETS as readonly string[]).includes(b);
    });
  }

  // Matches grouped by bucket in reviewer-facing order (strong → plausible → unlikely →
  // very_unlikely). Buckets that end up empty are still present as empty arrays; the slideout
  // decides whether to render a section based on the visible/filtered subset, not on this map.
  // A missing bucket is coerced to strong — same safety default as the server resolver.
  get matchesByBucket(): Record<MatchBucket, OffenderMatch[]> {
    const buckets: Record<MatchBucket, OffenderMatch[]> = {
      [MATCH_BUCKET.STRONG]: [],
      [MATCH_BUCKET.PLAUSIBLE]: [],
      [MATCH_BUCKET.UNLIKELY]: [],
      [MATCH_BUCKET.VERY_UNLIKELY]: [],
    };
    this.matches.forEach((m) => {
      const raw = (m.matchBucket ?? '').trim();
      const bucket: MatchBucket = raw === '' ? MATCH_BUCKET.STRONG : (raw as MatchBucket);
      if (buckets[bucket]) buckets[bucket].push(m);
    });
    return buckets;
  }

  // The patient name the check ran against — pulled from this instance's input snapshot so the
  // slideout heading reflects who was actually checked, even if workflow demographics later
  // change (OLIO-2391).
  get checkedName(): string {
    const value = (name: string) => this.inputs.find((i) => i.name === name)?.value ?? '';
    return `${value('patient_first_name')} ${value('patient_last_name')}`.trim();
  }
}
